import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "./checkout.css";
import { ref, push, onValue, query, orderByChild, limitToLast, get } from "firebase/database";
import { realtimeDb } from "../firebase/config";
import { calculateShippingCharges } from "../utils/shippingCalculator.js";
import { setPageMeta } from "../utils/seo";

// Load Razorpay script dynamically only on checkout page
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const existing = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}


const CheckoutPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Get cartItems and totalPrice from global state (window.cart for now)
  const cartItems = window.cart || [];
  const totalPrice = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  
  // Get applied coupon and shipping info from navigation state or calculate
  const location = useLocation();
  const appliedCoupon = location.state?.appliedCoupon || null;
  const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const subtotalAfterDiscount = totalPrice - discountAmount;
  
  // Calculate shipping charges
  const shippingInfo = location.state?.shippingInfo || calculateShippingCharges(subtotalAfterDiscount);
  const finalTotal = location.state?.finalTotal || (subtotalAfterDiscount + shippingInfo.shippingCharges);

  useEffect(() => {
    setPageMeta({
      title: "Checkout | Banat India",
      description:
        "Securely complete your purchase of Banat India toothbrushes and oral care products.",
      keywords:
        "checkout, secure payment, Banat India purchase, oral care products",
    });
  }, []);

  // Check if user is logged in, if not redirect to login
  useEffect(() => {
    if (!currentUser) {
      alert("Please sign in to proceed with checkout");
      navigate("/login");
      return;
    }
  }, [currentUser, navigate]);

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: "",
    lastName: "",
    email: "",
    phone: "",

    // Address
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  // Fetch last used address for the user
  useEffect(() => {
    if (currentUser && currentUser.uid) {
      const ordersRef = ref(realtimeDb, `orders/${currentUser.uid}`);
      const lastOrderQuery = query(ordersRef, orderByChild('createdAt'), limitToLast(1));
      onValue(lastOrderQuery, (snapshot) => {
        const orders = snapshot.val();
        if (orders) {
          const lastOrder = Object.values(orders)[0];
          if (lastOrder && lastOrder.shipping) {
            setFormData((prev) => ({
              ...prev,
              address: lastOrder.shipping.address || "",
              city: lastOrder.shipping.city || "",
              state: lastOrder.shipping.state || "",
              pincode: lastOrder.shipping.pincode || "",
              firstName: lastOrder.shipping.name?.split(" ")[0] || prev.firstName,
              lastName: lastOrder.shipping.name?.split(" ").slice(1).join(" ") || prev.lastName,
              email: lastOrder.shipping.email || prev.email,
              phone: lastOrder.shipping.phone || prev.phone,
            }));
          }
        }
      }, { onlyOnce: true });
    }
  }, [currentUser]);

  const [currentStep, setCurrentStep] = useState(1);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [errors, setErrors] = useState({});

  // Auto-redirect when payment is completed
  useEffect(() => {
    if (paymentCompleted) {
      const timer = setTimeout(() => {
        navigate("/dashboard?tab=orders");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [paymentCompleted, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName) newErrors.firstName = "First name is required";
      if (!formData.lastName) newErrors.lastName = "Last name is required";
      if (!formData.email) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email))
        newErrors.email = "Email is invalid";
      if (!formData.phone) newErrors.phone = "Phone number is required";
      else if (!/^\d{10}$/.test(formData.phone))
        newErrors.phone = "Phone number must be 10 digits";
    }

    if (step === 2) {
      if (!formData.address) newErrors.address = "Address is required";
      if (!formData.city) newErrors.city = "City is required";
      if (!formData.state) newErrors.state = "State is required";
      if (!formData.pincode) newErrors.pincode = "PIN code is required";
      else if (!/^\d{6}$/.test(formData.pincode))
        newErrors.pincode = "PIN code must be 6 digits";
    }

    return newErrors;
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    // If on address step, trigger Razorpay
    if (currentStep === 2) {
      // Prevent multiple payment attempts
      if (paymentCompleted) {
        return;
      }
      
      // Load Razorpay dynamically, then open checkout
      loadRazorpayScript().then(async (loaded) => {
        if (!loaded || !window.Razorpay) {
          alert('Payment gateway is not available. Please check your internet connection or try again later.');
          return;
        }
      
        try {
          // Check if Razorpay key is available
          if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
            throw new Error('Razorpay key not configured. Please set REACT_APP_RAZORPAY_KEY_ID environment variable.');
          }

          const requestBody = {
            amount: finalTotal,
            currency: "INR",
            receipt: `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          };
          
          // Create order with automatic capture with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const orderResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://shiprocket-backend-maeh.onrender.com'}/create-razorpay-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!orderResponse.ok) {
            const errorText = await orderResponse.text();
            throw new Error(`Backend error: ${orderResponse.status} - ${errorText}`);
          }
          
          const orderResult = await orderResponse.json();
          
          if (!orderResult.success) {
            throw new Error(orderResult.error || 'Failed to create order');
          }
          
          const orderId = orderResult.data.orderId;
          
          // Use Razorpay Checkout with automatic capture
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: finalTotal * 100,
            currency: "INR",
            name: "Banat India",
            description: "Order Payment",
            order_id: orderId,
            handler: function (response) {
              setPaymentCompleted(true);
              
              // Store payment info in Firebase
              push(ref(realtimeDb, "payments/"), {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                status: "success",
                createdAt: new Date().toISOString(),
                amount: finalTotal,
                email: formData.email,
                name: formData.firstName + ' ' + formData.lastName,
              });
              
              // Check if order already exists before creating
              const checkExistingOrder = async () => {
                try {
                  const ordersRef = ref(realtimeDb, `orders/${currentUser.uid}`);
                  const snapshot = await get(ordersRef);
                  
                  if (snapshot.exists()) {
                    const orders = snapshot.val();
                    const existingOrder = Object.values(orders).find(
                      order => order.paymentId === response.razorpay_payment_id
                    );
                    if (existingOrder) return;
                  }
                  
                  if (currentUser && currentUser.uid) {
                    push(ref(realtimeDb, `orders/${currentUser.uid}`), {
                      items: cartItems,
                      total: finalTotal,
                      subtotal: totalPrice,
                      shippingCharges: shippingInfo.shippingCharges,
                      discountAmount: discountAmount,
                      paymentId: response.razorpay_payment_id,
                      orderId: response.razorpay_order_id,
                      createdAt: new Date().toISOString(),
                      status: "paid",
                      shipping: {
                        address: formData.address,
                        city: formData.city,
                        state: formData.state,
                        pincode: formData.pincode,
                        name: formData.firstName + ' ' + formData.lastName,
                        email: formData.email,
                        phone: formData.phone,
                      },
                    });
                  }
                } catch {}
              };
              
              checkExistingOrder();
              
              // Clear the cart after successful payment
              if (window.cart && window.cart.length > 0) {
                localStorage.removeItem("guestCart");
                if (currentUser && currentUser.uid) {
                  import("../firebase/realtimeDb").then(({ saveUserCart }) => {
                    saveUserCart(currentUser.uid, []);
                  });
                }
                window.cart = [];
                window.__explicitlyClearedCart = true;
              }
              
              navigate("/payment-success", {
                state: {
                  paymentId: response.razorpay_payment_id,
                  order: {
                    items: cartItems,
                    total: finalTotal,
                    subtotal: totalPrice,
                    shippingCharges: shippingInfo.shippingCharges,
                    discountAmount: discountAmount,
                    shipping: {
                      address: formData.address,
                      city: formData.city,
                      state: formData.state,
                      pincode: formData.pincode,
                      name: formData.firstName + ' ' + formData.lastName,
                      email: formData.email,
                      phone: formData.phone,
                    },
                  },
                  userName: formData.firstName
                }
              });
            },
            prefill: {
              name: formData.firstName + ' ' + formData.lastName,
              email: formData.email,
              contact: formData.phone,
            },
            theme: { color: "#3399cc" },
          };
          
          const rzp = new window.Razorpay(options);
          rzp.open();
          
        } catch (error) {
          let errorMessage = 'Failed to create payment order. Please try again.';
          if (error.name === 'AbortError') {
            errorMessage = 'Request timeout. The payment service is taking too long to respond. Please try again.';
          } else if (error.message.includes('Razorpay key not configured')) {
            errorMessage = 'Payment gateway configuration error. Please contact support.';
          } else if (error.message.includes('Backend error')) {
            errorMessage = 'Payment service temporarily unavailable. Please try again in a few minutes.';
          } else if (error.message.includes('Failed to create order')) {
            errorMessage = 'Unable to process payment. Please check your internet connection and try again.';
          } else if (error.message.includes('fetch')) {
            errorMessage = 'Network error. Please check your internet connection and try again.';
          }
          alert(errorMessage);
        }
      });
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent duplicate orders if payment is already completed
    if (paymentCompleted) {
      navigate("/dashboard?tab=orders");
      return;
    }
    
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    // Store order info in Firebase Realtime Database for UPI and COD
    if (currentUser && currentUser.uid) {
      push(ref(realtimeDb, `orders/${currentUser.uid}`), {
        items: cartItems,
        total: finalTotal,
        subtotal: totalPrice,
        shippingCharges: shippingInfo.shippingCharges,
        discountAmount: discountAmount,
        paymentMethod: "UPI/COD",
        createdAt: new Date().toISOString(),
        status: "pending",
        shipping: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          name: formData.firstName + ' ' + formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
      });
    }

    // Clear the cart after successful order placement
    if (window.cart && window.cart.length > 0) {
      localStorage.removeItem("guestCart");
      if (currentUser && currentUser.uid) {
        import("../firebase/realtimeDb").then(({ saveUserCart }) => {
          saveUserCart(currentUser.uid, []);
        });
      }
      window.cart = [];
      window.__explicitlyClearedCart = true;
    }

    alert("Order placed successfully!");
    navigate("/dashboard?tab=orders");
  };

  const renderPersonalInfo = () => (
    <div className="checkout-section">
      <h3>Personal Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="firstName">First Name*</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={errors.firstName ? "error" : ""}
          />
          {errors.firstName && (
            <span className="error-message">{errors.firstName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name*</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={errors.lastName ? "error" : ""}
          />
          {errors.lastName && (
            <span className="error-message">{errors.lastName}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email*</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={errors.email ? "error" : ""}
          />
          {errors.email && (
            <span className="error-message">{errors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number*</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={errors.phone ? "error" : ""}
            maxLength="10"
          />
          {errors.phone && (
            <span className="error-message">{errors.phone}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderAddress = () => (
    <div className="checkout-section">
      <h3>Shipping Address</h3>
      <div className="form-grid">
        <div className="form-group full-width">
          <label htmlFor="address">Street Address*</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className={errors.address ? "error" : ""}
          />
          {errors.address && (
            <span className="error-message">{errors.address}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="city">City*</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            className={errors.city ? "error" : ""}
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="state">State*</label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className={errors.state ? "error" : ""}
          />
          {errors.state && (
            <span className="error-message">{errors.state}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="pincode">PIN Code*</label>
          <input
            type="text"
            id="pincode"
            name="pincode"
            value={formData.pincode}
            onChange={handleInputChange}
            className={errors.pincode ? "error" : ""}
            maxLength="6"
          />
          {errors.pincode && (
            <span className="error-message">{errors.pincode}</span>
          )}
        </div>
      </div>
    </div>
  );

  // Payment step and UI removed

  const getDeliveryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderOrderSummary = () => (
    <div className="order-summary">
      <h3>Order Summary</h3>
      <div className="summary-items">
        {cartItems.map((item) => (
          <div key={item.id} className="summary-item">
            <div className="item-info">
              <img src={item.image} alt={item.name} className="item-image" />
              <div className="item-details">
                <h4>{item.name}</h4>
                <p>Quantity: {item.quantity}</p>
              </div>
            </div>
            <div className="item-price">₹{item.price * item.quantity}</div>
          </div>
        ))}
      </div>
      <div className="summary-total">
        <div className="total-row">
          <span>Subtotal:</span>
          <span>₹{totalPrice}</span>
        </div>
        {appliedCoupon && (
          <div className="total-row discount-row">
            <span>Discount ({appliedCoupon.title}):</span>
            <span className="discount-amount">-₹{appliedCoupon.discountAmount}</span>
          </div>
        )}
        <div className="total-row shipping-row">
          <span>Shipping:</span>
          <span className={`shipping-amount ${shippingInfo.isFreeShipping ? 'free-shipping' : ''}`}>
            {shippingInfo.isFreeShipping ? 'Free' : `₹${shippingInfo.shippingCharges}`}
          </span>
        </div>
        <div className="total-row grand-total">
          <span>Total:</span>
          <span>₹{finalTotal}</span>
        </div>
        <div className="total-row estimated-delivery">
          Estimated Delivery: {getDeliveryDate()}
        </div>
        <div className="shipping-info-note">
          <div className="shipping-header">
            <h4>Shipping Charges</h4>
          </div>
          <div className="shipping-tiers">
            <div className="shipping-tier">
              <div className="tier-content">
                <span className="tier-range">Below ₹500</span>
                <span className="tier-price">₹100</span>
              </div>
            </div>
            <div className="shipping-tier">
              <div className="tier-content">
                <span className="tier-range">₹500 - ₹999</span>
                <span className="tier-price">₹75</span>
              </div>
            </div>
            <div className="shipping-tier free-tier">
              <div className="tier-content">
                <span className="tier-range">₹1000 & above</span>
                <span className="tier-price">Free Delivery</span>
              </div>
            </div>
          </div>
          <div className="shipping-tip">
            <strong>Pro Tip:</strong> Add ₹{Math.max(0, 1000 - subtotalAfterDiscount)} more to get FREE delivery!
          </div>
        </div>
        <div className="refund-policy-note">
          <p><strong>⚠️ Important:</strong> We do not provide refunds or cancellations for orders. Please review your order carefully before proceeding.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <div className="checkout-form">
          <div className="steps-indicator">
            <div className={`step ${currentStep >= 1 ? "active" : ""}`}>
              Personal Info
            </div>
            <div className={`step ${currentStep >= 2 ? "active" : ""}`}>
              Address
            </div>
            <div className={`step ${currentStep >= 3 ? "active" : ""}`}>
              Payment
            </div>
          </div>

          {/* Debug: Show current step number */}
          <div style={{ textAlign: 'center', marginBottom: '1rem', color: '#07b39b', fontWeight: 'bold' }}>
            Current Step: {currentStep}
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderPersonalInfo()}
            {currentStep === 2 && renderAddress()}
            {/* Payment step removed */}

            <div className="form-actions">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="back-button"
                >
                  Previous
                </button>
              )}
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="next-button"
                >
                  Next
                </button>
              ) : paymentCompleted ? (
                <div className="payment-completed-message">
                  Payment completed! Redirecting to dashboard...
                </div>
              ) : (
                <button type="submit" className="place-order-button">
                  Place Order
                </button>
              )}
            </div>
          </form>
        </div>
        {renderOrderSummary()}
      </div>
    </div>
  );
};

export default CheckoutPage;
